import { useSettingsStore } from '../state/settings.store';

// DOM walker for Reader Mode.
//
// A "block" is any element marked with `data-reader-block-id`. Within a
// surface, each block is a logical unit that the reading line can land on
// (a chat-message bubble, a markdown paragraph, a code block, a plan step).
// Anchors are { surfaceId, blockId, charOffset } — robust against scroll
// and re-renders, unlike screen-Y coordinates.

export interface ReaderAnchor {
  surfaceId: string;
  /** A stable identifier for the block; usually a message ID, plan-step ID,
   *  or a synthetic `block-${i}` if the surface didn't supply one. */
  blockId: string;
  /** Character offset INSIDE the block's text content. 0 = start of block. */
  charOffset: number;
}

export const READER_BLOCK_ATTR = 'data-reader-block-id';

/** Find every block element inside a container, in document order. */
export function listBlocks(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(`[${READER_BLOCK_ATTR}]`),
  );
}

/** Map a viewport Y coordinate to the nearest block element. Returns null if
 *  the container has no blocks (yet). */
export function blockAtScreenY(container: HTMLElement, screenY: number): HTMLElement | null {
  const blocks = listBlocks(container);
  if (blocks.length === 0) return null;

  // Find the block whose vertical center is closest to screenY.
  // Using closest-center (rather than "first block whose top > Y") is more
  // forgiving of large gaps between blocks and makes the line "snap" to a
  // block during a drag instead of jumping to the next one.
  let best: HTMLElement = blocks[0];
  let bestDist = Infinity;
  for (const b of blocks) {
    const r = b.getBoundingClientRect();
    const center = r.top + r.height / 2;
    const d = Math.abs(center - screenY);
    if (d < bestDist) { bestDist = d; best = b; }
  }
  return best;
}

/** Convert a screen Y position into a logical anchor inside the surface. */
export function screenYToAnchor(
  container: HTMLElement,
  surfaceId: string,
  screenY: number,
): ReaderAnchor | null {
  const block = blockAtScreenY(container, screenY);
  if (!block) return null;
  const blockId = block.getAttribute(READER_BLOCK_ATTR) || '';
  if (!blockId) return null;

  // Find approximate char offset by scanning text within the block and
  // picking the line whose vertical center is closest to screenY.
  const text = block.textContent || '';
  if (!text) return { surfaceId, blockId, charOffset: 0 };

  // For Phase 0 we snap to the start of the block. Sentence-precision
  // snapping is added in Phase 1 once tokenizer integration lands; the
  // anchor model already supports it.
  return { surfaceId, blockId, charOffset: 0 };
}

/** Convert an anchor into a screen Y coordinate (relative to the viewport).
 *  Used by ReaderOverlay to position the line on the page. */
export function anchorToScreenY(
  container: HTMLElement,
  anchor: ReaderAnchor,
): number | null {
  const block = container.querySelector<HTMLElement>(
    `[${READER_BLOCK_ATTR}="${cssEscape(anchor.blockId)}"]`,
  );
  if (!block) return null;
  const r = block.getBoundingClientRect();
  return r.top;
}

export type CodeBlockBehavior = 'skip' | 'announce' | 'full';

export function getCodeBlockBehavior(): CodeBlockBehavior {
  // settings-zustand-migration Phase 2: source = useSettingsStore.readerCodeBlocks
  // (migrated from the legacy 'reader-mode:code-blocks' key on first boot —
  // see settings.store.ts runLegacyMigration).
  return useSettingsStore.getState().readerCodeBlocks;
}

function isCodeBlock(el: HTMLElement): boolean {
  if (el.tagName === 'TABLE' && el.classList.contains('code-viewer-lines')) return true;
  if (el.querySelector('pre, code')) return true;
  return false;
}

function codeBlockAnnouncement(el: HTMLElement): string {
  const rows = el.querySelectorAll('tr');
  const lines = rows.length > 0 ? rows.length
    : (el.textContent || '').split('\n').filter((l) => l.trim()).length;
  return `Code block, ${lines} ${lines === 1 ? 'line' : 'lines'}.`;
}

/** Return the text from `anchor` to the end of the surface, concatenated as
 *  a single string. Each block contributes its `textContent`, separated by
 *  blank lines so the sentence tokenizer can keep them apart. */
export function textFromAnchor(
  container: HTMLElement,
  anchor: ReaderAnchor,
  codeBehavior?: CodeBlockBehavior,
): string {
  const behavior = codeBehavior ?? getCodeBlockBehavior();
  const blocks = listBlocks(container);
  const startIdx = blocks.findIndex(
    (b) => b.getAttribute(READER_BLOCK_ATTR) === anchor.blockId,
  );
  if (startIdx < 0) return '';

  const parts: string[] = [];
  for (let i = startIdx; i < blocks.length; i++) {
    const block = blocks[i];

    if (isCodeBlock(block)) {
      if (behavior === 'skip') continue;
      if (behavior === 'announce') { parts.push(codeBlockAnnouncement(block)); continue; }
      // 'full' — fall through to textContent
    }

    const t = (block.textContent || '').trim();
    if (!t) continue;
    if (i === startIdx && anchor.charOffset > 0 && anchor.charOffset < t.length) {
      parts.push(t.slice(anchor.charOffset));
    } else {
      parts.push(t);
    }
  }
  return parts.join('\n\n');
}

/** Convert a character offset in `textFromAnchor(container, startAnchor)` back
 *  into a ReaderAnchor. Used by Reader Mode to advance the line as sentences play.
 *
 *  `textFromAnchor` concatenates blocks with '\n\n', so each block contributes
 *  `block.length + 2` characters to the cumulative offset (except the last one).
 */
export function anchorAtOffset(
  container: HTMLElement,
  startAnchor: ReaderAnchor,
  targetOffset: number,
): ReaderAnchor | null {
  const blocks = listBlocks(container);
  const startIdx = blocks.findIndex(
    (b) => b.getAttribute(READER_BLOCK_ATTR) === startAnchor.blockId,
  );
  if (startIdx < 0) return null;

  let accumulated = 0;
  for (let i = startIdx; i < blocks.length; i++) {
    let t = (blocks[i].textContent || '').trim();
    if (!t) continue;
    if (i === startIdx && startAnchor.charOffset > 0 && startAnchor.charOffset < t.length) {
      t = t.slice(startAnchor.charOffset);
    }

    if (accumulated + t.length >= targetOffset) {
      const blockId = blocks[i].getAttribute(READER_BLOCK_ATTR) || '';
      const localOffset = targetOffset - accumulated;
      const charOffset = i === startIdx
        ? startAnchor.charOffset + localOffset
        : localOffset;
      return { surfaceId: startAnchor.surfaceId, blockId, charOffset };
    }
    accumulated += t.length + 2; // +2 for the '\n\n' separator
  }
  return null;
}

/** Escape a string so it can be used inside a CSS attribute selector. */
function cssEscape(s: string): string {
  // CSS.escape is widely supported; fallback to a manual implementation.
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}
